import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, CreditCard, ExternalLink, Globe2, Handshake, Info, Layers, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import PageHeader from '../components/shared/PageHeader';
import gdnLogo from '../assets/gdn-logo.jpeg';
import { useAuth } from '../hooks/useAuth';
import { useLocalization } from '../hooks/useLocalization';
import { COUNTRY_PRESETS, getCountryPreset } from '../lib/localization';
import { supabase } from '../lib/supabase';

export default function Configuracoes() {
  const { perfil } = useAuth();
  const { settings, refresh } = useLocalization();
  const [saving, setSaving] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [regional, setRegional] = useState(settings);
  const [brand, setBrand] = useState({ nome: '', logo: '' });

  useEffect(() => setRegional(settings), [settings]);
  useEffect(() => {
    async function loadBrand() {
      if (!perfil?.oficina_id) return;
      const { data } = await supabase
        .from('oficinas')
        .select('nome,logo')
        .eq('id', perfil.oficina_id)
        .maybeSingle();
      setBrand({ nome: data?.nome || '', logo: data?.logo || '' });
    }
    loadBrand();
  }, [perfil?.oficina_id]);

  const countryLabel = (code) => {
    try {
      return new Intl.DisplayNames([regional.locale], { type: 'region' }).of(code) || code;
    } catch {
      return code;
    }
  };

  const saveRegionalSettings = async () => {
    try {
      Intl.getCanonicalLocales(regional.locale);
      new Intl.NumberFormat(regional.locale, { style: 'currency', currency: regional.currency });
      new Intl.DateTimeFormat(regional.locale, { timeZone: regional.timeZone });
    } catch {
      toast.error('Revise o locale, a moeda ou o fuso horário informado.');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('oficinas')
        .update({
          pais_codigo: regional.countryCode,
          idioma: regional.language,
          locale_codigo: regional.locale,
          moeda: regional.currency.toUpperCase(),
          fuso_horario: regional.timeZone,
          sistema_medidas: regional.measurementSystem,
          formato_data: regional.dateFormat,
        })
        .eq('id', perfil.oficina_id);

      if (error) throw error;
      await refresh();
      toast.success('Preferencias regionais atualizadas.');
    } catch (error) {
      toast.error(error.message || 'Não foi possível salvar as preferências.');
    } finally {
      setSaving(false);
    }
  };

  const saveBrandSettings = async () => {
    if (!brand.nome.trim()) {
      toast.error('Informe o nome da oficina.');
      return;
    }

    setSavingBrand(true);
    try {
      const { error } = await supabase
        .from('oficinas')
        .update({
          nome: brand.nome.trim(),
          logo: brand.logo.trim() || null,
        })
        .eq('id', perfil.oficina_id);

      if (error) throw error;
      toast.success('Identidade da oficina atualizada.');
    } catch (error) {
      toast.error(error.message || 'Não foi possível salvar a identidade da oficina.');
    } finally {
      setSavingBrand(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Configuracoes" subtitle="Ajustes do sistema, regiao, planos, pagamento e suporte" />

      <Card>
        <div className="mb-4 flex items-start gap-3">
          <Info size={24} className="mt-0.5 text-orange-400" />
          <div>
            <h3 className="font-semibold">Identidade da oficina</h3>
            <p className="text-sm text-zinc-400">Nome e logo usados em documentos como o PDF da ordem de serviço.</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-[1fr_1.5fr_auto] md:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium">Nome da oficina</label>
            <Input value={brand.nome} onChange={(event) => setBrand((current) => ({ ...current, nome: event.target.value }))} placeholder="Nome da oficina" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">URL da logo</label>
            <Input value={brand.logo} onChange={(event) => setBrand((current) => ({ ...current, logo: event.target.value }))} placeholder="https://..." />
          </div>
          <Button onClick={saveBrandSettings} disabled={savingBrand || !perfil?.oficina_id}>
            {savingBrand ? 'Salvando...' : 'Salvar identidade'}
          </Button>
        </div>
        {brand.logo && (
          <img src={brand.logo} alt="Logo da oficina" className="mt-4 h-16 w-auto rounded-md border border-border bg-white object-contain p-2" />
        )}
      </Card>

      <Card>
        <div className="mb-4 flex items-start gap-3">
          <Globe2 size={24} className="mt-0.5 text-orange-400" />
          <div>
            <h3 className="font-semibold">Regiao e formatos</h3>
            <p className="text-sm text-zinc-400">Define moeda, datas, fuso horário, documentos e unidades para toda a oficina.</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium">Pais</label>
            <Select
              value={regional.countryCode}
              onChange={(event) => setRegional(getCountryPreset(event.target.value))}
              options={Object.keys(COUNTRY_PRESETS).map((code) => ({
                value: code,
                label: countryLabel(code),
              }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Idioma da interface</label>
            <Select
              value={regional.language}
              onChange={(event) => setRegional((current) => ({ ...current, language: event.target.value }))}
              options={[
                { value: 'pt-BR', label: 'Portugues (Brasil)' },
                { value: 'pt-PT', label: 'Portugues (Portugal)' },
                { value: 'en-US', label: 'English (US)' },
                { value: 'en-GB', label: 'English (UK)' },
                { value: 'es-ES', label: 'Espanol' },
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Locale</label>
            <Input value={regional.locale} onChange={(event) => setRegional((current) => ({ ...current, locale: event.target.value }))} placeholder="Ex: en-US" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Moeda (ISO 4217)</label>
            <Input value={regional.currency} onChange={(event) => setRegional((current) => ({ ...current, currency: event.target.value.toUpperCase().slice(0, 3) }))} placeholder="Ex: USD" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Fuso horário (IANA)</label>
            <Input value={regional.timeZone} onChange={(event) => setRegional((current) => ({ ...current, timeZone: event.target.value }))} placeholder="Ex: America/New_York" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Sistema de medidas</label>
            <Select
              value={regional.measurementSystem}
              onChange={(event) => setRegional((current) => ({ ...current, measurementSystem: event.target.value }))}
              options={[
                { value: 'metric', label: 'Metrico (km)' },
                { value: 'imperial', label: 'Imperial (mi)' },
              ]}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={saveRegionalSettings} disabled={saving || !perfil?.oficina_id}>
            {saving ? 'Salvando...' : 'Salvar preferencias regionais'}
          </Button>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <Info size={22} className="mb-3 text-orange-400" />
          <h3 className="font-semibold">Sobre o sistema</h3>
          <p className="mt-1 text-sm text-zinc-400">Versao, descricao do MecSync e informacoes da GDN Corp.</p>
          <Link to="/configuracoes/sobre"><Button className="mt-3">Abrir Sobre</Button></Link>
        </Card>
        <Card>
          <Layers size={22} className="mb-3 text-orange-400" />
          <h3 className="font-semibold">Planos</h3>
          <p className="mt-1 text-sm text-zinc-400">Beneficios disponiveis para cada plano do MecSync.</p>
          <Link to="/configuracoes/planos"><Button className="mt-3">Ver planos</Button></Link>
        </Card>
        <Card>
          <CreditCard size={22} className="mb-3 text-orange-400" />
          <h3 className="font-semibold">Pagamento</h3>
          <p className="mt-1 text-sm text-zinc-400">Plano atual, vencimento, status e histórico de pagamentos.</p>
          <Link to="/configuracoes/pagamento"><Button className="mt-3">Abrir pagamento</Button></Link>
        </Card>
        <Card>
          <Handshake size={22} className="mb-3 text-orange-400" />
          <h3 className="font-semibold">Parceiros</h3>
          <p className="mt-1 text-sm text-zinc-400">Acesse o cadastro comercial para indicar vendedores MecSync.</p>
          <Link to="/parceiros"><Button className="mt-3">Abrir parceiros</Button></Link>
        </Card>
      </div>

      <Card>
        <h3 className="mb-3 font-display text-2xl">Suporte <span className="text-sky-400">GDN Corp</span></h3>
        <img src={gdnLogo} alt="GDN Corp" className="mb-3 h-[100px] w-auto rounded-md bg-[#0f172a] object-contain p-2" />
        <p className="mb-4 text-sm text-zinc-300">Precisa de ajuda? Nossa equipe esta pronta para atender.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => window.open('mailto:contato@gdncorp.com.br', '_blank', 'noopener,noreferrer')}>
            <Mail size={16} /> Email de suporte
          </Button>
          <Button variant="ghost" onClick={() => window.open('https://gdncorp.com.br', '_blank', 'noopener,noreferrer')}>
            <ExternalLink size={16} /> Site GDN Corp
          </Button>
        </div>
      </Card>

      <Card className="border-yellow-400/20 bg-yellow-400/5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-400/10 text-yellow-300"><Bot size={24} /></div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-yellow-100">MecSync Premium IA</h3>
            <p className="mt-1 text-sm text-zinc-300">Automacao de atendimento, CRM, triagem com IA e funil comercial integrado.</p>
          </div>
          <img src="/synes-ia-logo-transparent.png" alt="Synes.ia" className="h-12 w-auto rounded object-contain p-1 drop-shadow-lg" />
        </div>
      </Card>
    </div>
  );
}
